import type {CreativeEngine} from "@cesdk/cesdk-js";
import {DYNAMIC_VARIABLES_UPDATED_EVENT} from "../constants.ts";

export type DynamicValueType = "text" | "number" | "currency" | "percent";

export type RuntimeDynamicVariableEntry = {
    value: string;
    valueType?: DynamicValueType;
};

const DYNAMIC_VALUE_TYPES = new Set<DynamicValueType>(["text", "number", "currency", "percent"]);

export const normalizeDynamicValueType = (value: unknown): DynamicValueType | undefined => {
    if (typeof value !== "string") return undefined;
    return DYNAMIC_VALUE_TYPES.has(value as DynamicValueType)
        ? (value as DynamicValueType)
        : undefined;
};

export const isRuntimeDynamicVariableEntry = (value: unknown): value is RuntimeDynamicVariableEntry => {
    if (!value || typeof value !== "object") return false;
    const entry = value as { value?: unknown; valueType?: unknown };
    return typeof entry.value === "string" && normalizeDynamicValueType(entry.valueType) === entry.valueType;
};

export const readWindowDynamicVariableEntry = (name: string): RuntimeDynamicVariableEntry | null => {
    const trimmedName = String(name ?? "").trim();
    if (!trimmedName || typeof window === "undefined") return null;

    const rawEntry = (window as any).__ClickVars?.[trimmedName];
    if (rawEntry == null) return null;

    if (isRuntimeDynamicVariableEntry(rawEntry)) {
        return {
            value: rawEntry.value,
            valueType: rawEntry.valueType
        };
    }

    const rawValue = String(rawEntry ?? "");
    if (rawValue.trim() === "") return null;
    return { value: rawValue };
};

export const writeChoiceVariable = (
    engine: CreativeEngine | null | undefined,
    name: string,
    value: string,
    valueType?: unknown
): RuntimeDynamicVariableEntry | null => {
    const trimmedName = String(name ?? "").trim();
    if (!trimmedName) return null;

    const storedValue = String(value ?? "");
    const normalizedType = normalizeDynamicValueType(valueType);

    try { engine?.variable?.setString?.(`choice:${trimmedName}`, storedValue); } catch {}
    try { engine?.variable?.setString?.(trimmedName, storedValue); } catch {}
    try { engine?.variable?.setString?.(`choice:${trimmedName}:type`, normalizedType ?? ""); } catch {}
    try { engine?.variable?.setString?.(`${trimmedName}:type`, normalizedType ?? ""); } catch {}

    if (typeof window !== "undefined") {
        (window as any).__ClickVars ??= {};
        (window as any).__ClickVars[trimmedName] = normalizedType
            ? { value: storedValue, valueType: normalizedType }
            : { value: storedValue };
        window.dispatchEvent(new CustomEvent(DYNAMIC_VARIABLES_UPDATED_EVENT, {
            detail: {
                name: trimmedName,
                value: storedValue,
                valueType: normalizedType
            }
        }));
    }

    return normalizedType
        ? { value: storedValue, valueType: normalizedType }
        : { value: storedValue };
};
